import React, { useMemo, useState } from "react";
import {
  FaImage,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrashAlt,
  FaUpload,
} from "react-icons/fa";

import {
  getAlbumPhotos,
  getMediaUrl,
} from "../utils/albumsPhotosUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

export default function PhotosManager({
  album,
  saving = false,
  onAddPhoto,
  onDeletePhoto,
  onClose,
}) {
  const photos = useMemo(() => getAlbumPhotos(album), [album]);

  const [photoUrl, setPhotoUrl] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [deleteCandidateId, setDeleteCandidateId] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);

    if (!file) {
      setPreviewUrl(photoUrl ? getMediaUrl(photoUrl) : "");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handlePhotoUrlChange = (event) => {
    const value = event.target.value;

    setPhotoUrl(value);

    if (!imageFile) {
      setPreviewUrl(getMediaUrl(value));
    }
  };

  const resetForm = () => {
    setPhotoUrl("");
    setDescription("");
    setImageFile(null);
    setPreviewUrl("");
  };

  const handleAdd = async (event) => {
    event.preventDefault();

    await onAddPhoto({
      albumId: album.id,
      photo_url: photoUrl,
      description,
      imageFile,
    });

    resetForm();
  };

  const handleDelete = async (photoId) => {
    await onDeletePhoto({
      albumId: album.id,
      photoId,
    });

    setDeleteCandidateId(null);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Photos de l’album
            </h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {photos.length} photo{photos.length > 1 ? "s" : ""} dans “
              {album.titre}”.
            </p>
          </div>
        </div>

        {photos.length ? (
          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {photos.map((photo) => {
              const isDeleting = deleteCandidateId === photo.id;

              return (
                <article
                  key={photo.id || photo.photo_url}
                  className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-950"
                >
                  <div className="relative h-48 bg-slate-100 dark:bg-slate-800">
                    {photo.photo_url ? (
                      <img
                        src={getMediaUrl(photo.photo_url)}
                        alt={photo.description || album.titre}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center text-slate-400">
                        <FaImage className="text-4xl" />
                        <p className="mt-3 text-sm font-medium">
                          Photo indisponible
                        </p>
                      </div>
                    )}

                    <div className="absolute right-3 top-3">
                      {isDeleting ? (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleDelete(photo.id)}
                            className="rounded-xl bg-rose-600 px-3 py-2 text-xs font-bold text-white shadow-lg shadow-black/20 transition hover:bg-rose-700 disabled:opacity-60"
                          >
                            Oui
                          </button>

                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setDeleteCandidateId(null)}
                            className="rounded-xl bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-lg shadow-black/20 transition hover:bg-slate-100 disabled:opacity-60"
                          >
                            Non
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => setDeleteCandidateId(photo.id)}
                          className="grid h-10 w-10 place-items-center rounded-xl bg-rose-600 text-white shadow-lg shadow-black/20 transition hover:bg-rose-700 disabled:opacity-60"
                          title="Supprimer la photo"
                        >
                          <FaTrashAlt />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-4">
                    <p className="line-clamp-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                      {photo.description || "Aucune description"}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="mt-5 rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-950">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <FaImage className="text-2xl" />
            </div>

            <h4 className="mt-4 font-bold text-slate-950 dark:text-white">
              Aucune photo pour le moment
            </h4>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Ajoutez une première photo pour illustrer cet album.
            </p>
          </div>
        )}
      </section>

      <form
        onSubmit={handleAdd}
        className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900"
      >
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Ajouter une photo
        </h3>

        <div className="mt-4 grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Aperçu nouvelle photo"
                className="h-52 w-full object-cover"
              />
            ) : (
              <div className="flex h-52 flex-col items-center justify-center text-slate-400">
                <FaImage className="text-4xl" />
                <p className="mt-3 text-sm font-medium">Aucun aperçu</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Télécharger une photo</label>

              <label className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center transition hover:border-orange-300 hover:bg-orange-50/50 dark:border-slate-700 dark:bg-slate-950 dark:hover:border-orange-500/40 dark:hover:bg-orange-500/10">
                <FaUpload className="text-xl text-orange-500" />
                <span className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  Choisir un fichier
                </span>
                <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  JPG, PNG ou WEBP recommandé
                </span>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="sr-only"
                />
              </label>
            </div>

            <div>
              <label className={labelClass}>Ou URL / chemin de la photo</label>
              <input
                type="text"
                value={photoUrl}
                onChange={handlePhotoUrlChange}
                placeholder="/uploads/photos/photo.jpg ou https://..."
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Description</label>
              <input
                type="text"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Ex : Remise des ceintures, tournoi, entraînement..."
                className={inputClass}
              />
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={resetForm}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Réinitialiser
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            {saving ? <FaSave /> : <FaPlus />}
            {saving ? "Ajout en cours..." : "Ajouter la photo"}
          </button>
        </div>
      </form>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}